import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { html, nothing } from 'lit';
import { hasFeature } from '../assessment/features.js';
import { toneDot } from '../campaign-status.js';
import { describeSeat, seatLook } from '../linkedin-seat.js';
import { openAuthPopup } from '../oauth-popup.js';
import {
  askAndDeleteSalesRep,
  askAndDisconnectAccount,
  DISCONNECT_ACCOUNT_HINT,
  loadFailedNotice,
  NOTHING_FAILED,
  type AccountToDisconnect,
  type SettingsLoadFailures,
  warmupCapLabel,
} from './settings-safeguards.js';
import {
  type ConnectDeps,
  connectLinkedin as connectLinkedinAccount,
  connectMailbox,
  type RepIdTarget,
  setMailboxWarmed as storeMailboxWarmed,
} from './settings-accounts.js';
import {
  connectHubspot as connectHubspotPortal,
  type MappingBusy,
  type MappingDeps,
  disconnectCrm as askAndDisconnectCrm,
  saveMapping as storeMapping,
  suggestMapping as applySuggestedMapping,
  toggleMapping as toggleMappingPanel,
} from './settings-crm.js';
import { type Say, warn } from './settings-flow.js';
import { type ConnectedEmail, type ConnectedLinkedin, type CrmConfigView, loadSettingsLists } from './settings-load.js';
import {
  NAME_FIRST,
  nameMissing,
  removeOrphanRep,
  repForConnect,
  saveSalesRep as storeSalesRep,
} from './settings-reps.js';
import {
  type BinDeps,
  type DeletedCampaign,
  loadBin,
  purgeCampaign as askAndPurgeCampaign,
  restoreCampaign as restoreDeletedCampaign,
} from './settings-trash.js';
import { skeletonLine } from '../skeleton.js';
import { toast } from '../toast.js';
import type { SalesRep } from '../wizard/types.js';
// CRM provider info, the connections and the Lead Status mapping, over REST (R11b).
import {
  type CrmConnectionView,
  disconnectCrmConnection,
  getCrmConfig,
  getHubspotLeadStatusMapping,
  type HubspotLeadStatusMappingView,
  listCrmConnections,
  saveHubspotLeadStatusMapping,
  suggestHubspotLeadStatusMapping,
} from '../crm-api.js';

// The page's flows live in modules of their own (R18b, settings-flow.ts lists them);
// this page holds its state, hands each flow what it needs, and draws.

type CrmConnection = CrmConnectionView;

export const Settings = stateful(() => {
  const [emailAccounts, setEmailAccounts] = useState<ConnectedEmail[]>([]);
  const [linkedinAccounts, setLinkedinAccounts] = useState<ConnectedLinkedin[]>([]);
  const [error, setError] = useState('');
  // Local 'success' state retained so existing setSuccess('') clears between flows; toasts are the user-visible feedback.
  const [, setSuccess] = useState('');
  const notifySuccess = (m: string) => {
    setSuccess(m);
    toast.success(m);
  };
  const notifyError = (m: string) => {
    setError(m);
    toast.error(m);
  };
  /** How every flow tells the person. */
  const say: Say = { success: notifySuccess, error: notifyError, warning: m => toast.warning(m) };
  const clearMessages = () => {
    setError('');
    setSuccess('');
  };
  const [salesReps, setSalesReps] = useState<SalesRep[]>([]);
  const [editingEmployee, setEditingEmployee] = useState<Partial<SalesRep> | null>(null);
  // The editor as it is now (R18b). A connect started from the editor creates
  // its rep only once the account is connected, from the render of the click,
  // seconds old: what the person typed since then lives here, not there. The
  // session changes each time an editor opens, so a connect reads only the
  // editor it was started from, and only while it is open.
  const [editorNow] = useState({ profile: null as Partial<SalesRep> | null, session: 0 });
  editorNow.profile = editingEmployee;
  const editorSession = editorNow.session;
  const [savingEmployee, setSavingEmployee] = useState(false);
  // Id of a Sales Rep that was implicitly created so the connect flow could
  // attach an account to it (OAuth assignment needs the rep id). Tracked so we
  // can clean it up if the user cancels before connecting any account.
  const [autoCreatedRepId, setAutoCreatedRepId] = useState<string | null>(null);
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [connectingEmail, setConnectingEmail] = useState(false);
  const [connectingLinkedin, setConnectingLinkedin] = useState(false);

  // CRM integrations
  const [crmConfig, setCrmConfig] = useState<CrmConfigView>({ hubspot: { enabled: false } });
  const [crmConnections, setCrmConnections] = useState<CrmConnection[]>([]);
  const [connectingCrm, setConnectingCrm] = useState(false);

  // HubSpot Lead Status mapping panel (Teil 2): open connection + its loaded view
  // + the operator's draft (localStatus -> chosen option value, '' = leave unset).
  const [mappingConnId, setMappingConnId] = useState<string | null>(null);
  const [mappingView, setMappingView] = useState<HubspotLeadStatusMappingView | null>(null);
  const [mappingDraft, setMappingDraft] = useState<Record<string, string>>({});
  const [mappingBusy, setMappingBusy] = useState<MappingBusy>('');
  const [mappingError, setMappingError] = useState<string | null>(null);

  // Locked / "coming soon" CRM providers — UI only, gated behind sales request.
  const lockedCrmProviders: { id: string; name: string; icon: string }[] = [
    { id: 'salesforce', name: 'Salesforce', icon: 'icon-[material-symbols--cloud]' },
    { id: 'zoho', name: 'Zoho CRM', icon: 'icon-[material-symbols--workspaces]' },
    { id: 'pipedrive', name: 'Pipedrive', icon: 'icon-[material-symbols--trending-up]' },
    { id: 'dynamics365', name: 'Dynamics 365', icon: 'icon-[material-symbols--grid-view]' },
    { id: 'monday', name: 'monday CRM', icon: 'icon-[material-symbols--calendar-month]' },
  ];

  // True until the first load has run: an empty list before it is not "none" (R18a).
  const [loading, setLoading] = useState(true);
  // Which lists did not load, so their empty state does not stand in for the failure (R18a).
  const [loadFailed, setLoadFailed] = useState<SettingsLoadFailures>(NOTHING_FAILED);
  // The CRM section keeps its last answer while it reloads, so it waits only for the first.
  const [loadedOnce, setLoadedOnce] = useState(false);

  // ── Deleted campaigns (the recycle bin) ───────────────────────────────
  const [deletedCampaigns, setDeletedCampaigns] = useState<DeletedCampaign[]>([]);
  const [retentionDays, setRetentionDays] = useState(30);
  const [loadingDeleted, setLoadingDeleted] = useState(true);
  const [deletedFailed, setDeletedFailed] = useState(false);
  /** Campaign id with a restore/purge request in flight. */
  const [deletedBusy, setDeletedBusy] = useState('');

  // ── Assessment Agent (NDI-239 v2): its card shows only with the flag ──
  const [assessmentOn, setAssessmentOn] = useState(false);

  // Load accounts after first render
  useEffect(() => {
    void loadAccounts();
    void loadDeletedCampaigns();
    void hasFeature('assessmentAgent').then(setAssessmentOn);
  }, []);

  // ── The recycle bin (settings-trash.ts) ───────────────────────────────
  async function loadDeletedCampaigns() {
    setLoadingDeleted(true);
    const bin = await loadBin();
    if (bin) {
      setDeletedCampaigns(bin.campaigns);
      if (bin.retentionDays !== null) setRetentionDays(bin.retentionDays);
    }
    setDeletedFailed(bin === null);
    setLoadingDeleted(false);
  }

  const binDeps: BinDeps = {
    say,
    setBusy: setDeletedBusy,
    removed: id => setDeletedCampaigns(deletedCampaigns.filter(x => x._id !== id)),
  };
  const restoreCampaign = (c: DeletedCampaign) => restoreDeletedCampaign(c, binDeps);
  const purgeCampaign = (c: DeletedCampaign) => askAndPurgeCampaign(c, binDeps);

  // ── The lists (settings-load.ts) ──────────────────────────────────────
  async function loadAccounts() {
    setLoading(true);
    try {
      const lists = await loadSettingsLists({ getCrmConfig, listCrmConnections });
      setEmailAccounts(lists.emailAccounts);
      if (lists.linkedinAccounts) setLinkedinAccounts(lists.linkedinAccounts);
      if (lists.salesReps) setSalesReps(lists.salesReps);
      setCrmConfig(lists.crmConfig);
      setCrmConnections(lists.crmConnections);
      setLoadFailed(lists.loadFailed);
      setLoadedOnce(true);
    } finally {
      setLoading(false);
    }
  }

  // ── Sales Reps (settings-reps.ts) ─────────────────────────────────────
  const saveSalesRep = () => {
    if (!editingEmployee) return;
    return storeSalesRep(editingEmployee, {
      say,
      setSaving: setSavingEmployee,
      clearError: () => setError(''),
      saved: () => {
        setEditingEmployee(null);
        setAutoCreatedRepId(null);
      },
      reload: loadAccounts,
    });
  };

  // Asks once (it asked twice: the confirmation was written out two times), and
  // names the rep and what happens to its accounts (R18a).
  async function deleteSalesRep(rep: SalesRep, assigned: { email: number; linkedin: number }) {
    const outcome = await askAndDeleteSalesRep(rep, loadFailed.email || loadFailed.linkedin ? null : assigned);
    if (outcome === 'cancelled') return;
    if (outcome === 'done') {
      notifySuccess('Sales Representative deleted.');
      void loadAccounts();
    } else {
      notifyError(outcome.error);
    }
  }

  /** Whether the given Sales Rep has at least one email or LinkedIn account assigned. */
  const repHasAccount = (repId: string | null | undefined): boolean =>
    !!repId &&
    (emailAccounts.some(a => a.aiEmployeeId === repId) || linkedinAccounts.some(a => a.aiEmployeeId === repId));

  /** Open the profile editor for a new or existing Sales Rep (resets connect-flow state). */
  function openEmployeeEditor(emp: Partial<SalesRep>) {
    editorNow.session++;
    setEditingEmployee(emp);
    setAutoCreatedRepId(null);
  }

  /**
   * The rep in the editor, persisted so an account can be assigned to it: a
   * brand-new rep is created first and remembered as auto-created (for the
   * clean-up on Cancel). Runs only once the account is connected.
   */
  const ensureEditingRepPersisted = (): Promise<string | null> => {
    // The editor this connect started from, as it is now. When that editor was
    // closed or another opened meanwhile, the profile as it was at the click.
    const open = () => editorNow.session === editorSession && editorNow.profile !== null;
    const live = open() ? editorNow.profile : null;
    // The click checked the name. A name cleared since then must not leave the connected account with nobody
    // (R18b review): the rep is named as at the click, and the editor keeps what was typed.
    const cleared = live !== null && nameMissing(live);
    const profile = live && !cleared ? live : editingEmployee;
    if (!live) warn('connect finished after its editor was closed: the rep is the profile as it was at the click', {});
    if (cleared) warn('connect finished with the name cleared: the rep is named as it was at the click', {});
    return repForConnect(profile, {
      say,
      created: id => {
        // What was typed while the rep was being created stays too (R18b review): the editor as it is now,
        // while it is still the one this connect started from.
        setEditingEmployee({ ...(open() ? editorNow.profile : profile), _id: id });
        setAutoCreatedRepId(id);
      },
      reload: loadAccounts,
    });
  };

  /**
   * Close the profile editor. If we implicitly created a rep for the connect
   * flow but it never got an account assigned (e.g. the user closed the OAuth
   * popup), remove it again so no account-less rep is left behind.
   */
  async function cancelEmployeeEditor() {
    const orphanId = autoCreatedRepId;
    setEditingEmployee(null);
    setAutoCreatedRepId(null);
    await removeOrphanRep(orphanId, repHasAccount(orphanId), { reload: loadAccounts });
  }

  // ── Sending accounts (settings-accounts.ts) ───────────────────────────
  const accountDeps = { say, reload: loadAccounts };
  const setMailboxWarmed = (accountId: string, warmed: boolean) => storeMailboxWarmed(accountId, warmed, accountDeps);

  const connectDeps = (setConnecting: (busy: boolean) => void): ConnectDeps => ({
    ...accountDeps,
    openPopup: openAuthPopup,
    setConnecting,
    clearMessages,
  });
  // Not async: each opens its sign-in popup before it returns, inside the click (oauth-popup.ts).
  const connectUnipileEmail = (forAiEmployee?: RepIdTarget, emailProvider: 'google' | 'microsoft' = 'google') =>
    connectMailbox(forAiEmployee, emailProvider, connectDeps(setConnectingEmail));
  const connectLinkedin = (forAiEmployee?: RepIdTarget) =>
    connectLinkedinAccount(forAiEmployee, connectDeps(setConnectingLinkedin));

  // --- Disconnect ---
  // Asks first and says what happens (it deleted at once): our row goes, the
  // account stays connected in Unipile, which 2.1 and 2.0 share (R18a).
  async function disconnectAccount(account: AccountToDisconnect) {
    setError('');
    setSuccess('');
    const outcome = await askAndDisconnectAccount(account);
    if (outcome === 'cancelled') return;
    if (outcome === 'done') {
      notifySuccess('Account disconnected.');
      void loadAccounts();
    } else {
      notifyError(outcome.error);
    }
  }

  // ── CRM (settings-crm.ts) ─────────────────────────────────────────────
  const connectHubspot = () =>
    connectHubspotPortal({
      say,
      openPopup: openAuthPopup,
      storage: localStorage,
      origin: window.location.origin,
      setConnecting: setConnectingCrm,
      clearMessages,
      reload: loadAccounts,
    });

  const disconnectCrm = (connectionId: string, label: string) =>
    askAndDisconnectCrm(connectionId, label, { say, disconnectCrmConnection, reload: loadAccounts });

  const mappingDeps: MappingDeps = {
    say,
    getHubspotLeadStatusMapping,
    suggestHubspotLeadStatusMapping,
    saveHubspotLeadStatusMapping,
    setOpen: setMappingConnId,
    setView: setMappingView,
    setDraft: setMappingDraft,
    setBusy: setMappingBusy,
    setError: setMappingError,
  };
  const toggleMapping = (connectionId: string) => toggleMappingPanel(connectionId, mappingConnId, mappingDeps);
  const suggestMapping = () => applySuggestedMapping(mappingConnId, mappingDeps);
  const saveMapping = () => storeMapping(mappingConnId, mappingDraft, mappingDeps);

  const renderMappingPanel = () => html`
    <div class="border-base-300 bg-base-100 mt-2 rounded-lg border p-3">
      <div class="mb-1 flex items-center justify-between gap-2">
        <div class="flex items-center gap-2">
          <span class="icon-[material-symbols--rule] text-base-content/50 text-sm"></span>
          <span class="text-sm font-medium">Lead Status mapping</span>
          ${
            mappingView?.hasSaved
              ? toneDot({ tone: 'positive', label: 'Saved' })
              : nothing
          }
        </div>
        ${
          mappingView?.optionsReadable
            ? html`<button
                class="btn btn-ghost btn-xs gap-1"
                ?disabled=${mappingBusy !== ''}
                title="Let AI propose a mapping from this portal's option labels"
                @click=${() => void suggestMapping()}
              >
                ${
                  mappingBusy === 'suggest'
                    ? html`<span class="loading loading-spinner loading-xs"></span>`
                    : html`<span class="icon-[material-symbols--auto-awesome] text-sm"></span>`
                }
                Suggest with AI
              </button>`
            : nothing
        }
      </div>
      <p class="text-base-content/50 mb-3 text-xs">
        Choose which HubSpot Lead Status each of our states writes when a lead is pushed. Leave a state on “Don't
        set” to skip it.
      </p>
      ${
        mappingBusy === 'load' && !mappingView
          ? html`<div class="text-base-content/50 flex items-center gap-2 py-2 text-xs">
              <span class="loading loading-spinner loading-xs"></span> Loading the portal's options…
            </div>`
          : mappingError
            ? html`<div class="alert alert-error px-3 py-2 text-xs" role="alert">${mappingError}</div>`
            : mappingView && !mappingView.optionsReadable
              ? html`<div class="alert alert-warning items-start px-3 py-2 text-xs" role="alert">
                  <span class="icon-[material-symbols--warning-outline] text-base"></span>
                  <div>
                    <div class="font-medium">Couldn't read this portal's Lead Status options.</div>
                    ${mappingView.readError ? html`<div class="mt-0.5 opacity-80">${mappingView.readError}</div>` : nothing}
                    <div class="mt-1 opacity-80">
                      The connection likely needs the property-read scope — grant it and reconnect, then reopen this
                      panel.
                    </div>
                  </div>
                </div>`
              : mappingView
                ? html`
                    <div class="flex flex-col gap-2">
                      ${(mappingView.rows ?? []).map(
                        row => html`
                          <div class="flex items-center gap-2">
                            <span class="badge badge-ghost badge-sm w-24 shrink-0 justify-start font-medium">
                              ${row.localLabel}
                            </span>
                            <span
                              class="icon-[material-symbols--arrow-forward] text-base-content/30 shrink-0 text-sm"
                            ></span>
                            <select
                              class="select select-bordered select-sm min-w-0 flex-1"
                              @change=${(e: Event) =>
                                setMappingDraft({
                                  ...mappingDraft,
                                  [row.localStatus]: (e.target as HTMLSelectElement).value,
                                })}
                            >
                              <option value="" ?selected=${!mappingDraft[row.localStatus]}>— Don't set —</option>
                              ${(mappingView?.options ?? []).map(
                                o => html`<option value=${o.value} ?selected=${mappingDraft[row.localStatus] === o.value}>
                                  ${o.label}
                                </option>`,
                              )}
                            </select>
                          </div>
                        `,
                      )}
                    </div>
                    <div class="mt-3 flex items-center justify-between gap-2">
                      <span class="text-base-content/40 text-xs">
                        ${
                          mappingView.aiSuggested
                            ? 'AI-suggested — review before saving.'
                            : mappingView.hasSaved
                              ? nothing
                              : 'Prefilled from a best-guess match.'
                        }
                      </span>
                      <button
                        class="btn btn-primary btn-sm gap-1"
                        ?disabled=${mappingBusy !== ''}
                        @click=${() => void saveMapping()}
                      >
                        ${mappingBusy === 'save' ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing}
                        Save mapping
                      </button>
                    </div>
                  `
                : nothing
      }
    </div>
  `;

  return html`
    <div class="mx-auto flex max-w-4xl flex-col gap-6 p-6">
      <div class="flex flex-col gap-2">
        <h1 class="text-[2rem] leading-10 font-medium tracking-tight">Settings</h1>
        <p class="text-base-content/60">Manage your Sales Representatives and the sending accounts they use.</p>
      </div>

      ${error && !loading ? html`<div class="alert alert-error text-sm" role="alert">${error}</div>` : nothing}

      <!-- Sales Representative Profiles -->
      <div class="card bg-base-100">
        <div class="card-body p-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
                <span class="icon-[material-symbols--smart-toy] text-xl"></span>
              </span>
              <div>
                <h2 class="text-[0.9375rem] font-medium">Sales representatives</h2>
                <p class="text-base-content/70 text-sm">
                  Each Sales Representative has its own persona and sending accounts.
                </p>
              </div>
            </div>
            <button
              class="btn btn-primary btn-sm gap-1"
              @click=${() =>
                openEmployeeEditor({
                  name: '',
                  role: '',
                  company: '',
                  communicationStyle: '',
                  signOff: '',
                  linkedinSignOff: '',
                  isDefault: false,
                })}
            >
              <span class="icon-[material-symbols--add] text-lg"></span> New Sales Representative
            </button>
          </div>

          ${
            loading
              ? html`<div class="mt-4 flex flex-col gap-2">
                ${skeletonLine({ width: 'w-full', height: 'h-14' })}
                ${skeletonLine({ width: 'w-full', height: 'h-14' })}
              </div>`
              : loadFailed.reps
                ? html`<div class="mt-4">${loadFailedNotice('Sales representatives', () => void loadAccounts())}</div>`
                : salesReps.length === 0
                ? html`<div class="border-base-300 mt-4 rounded-lg border border-dashed p-6 text-center">
                  <p class="text-base-content/60 text-sm">
                    No Sales Representatives yet. Each Sales Representative is a sender persona that owns email and
                    LinkedIn accounts.
                  </p>
                  <button
                    class="btn btn-primary btn-sm mt-3 gap-1"
                    @click=${() =>
                      openEmployeeEditor({
                        name: '',
                        role: '',
                        company: '',
                        communicationStyle: '',
                        signOff: '',
                        isDefault: false,
                      })}
                  >
                    <span class="icon-[material-symbols--add] text-base" aria-hidden="true"></span>
                    Create your first Sales Representative
                  </button>
                </div>`
                : html`<div class="mt-4 flex flex-col gap-3">
                  ${salesReps.map(emp => {
                    const isExpanded = expandedEmployeeId === emp._id;
                    const assignedEmails = emailAccounts.filter(a => a.aiEmployeeId === emp._id);
                    const assignedLinkedin = linkedinAccounts.filter(a => a.aiEmployeeId === emp._id);
                    return html`
                      <div
                        class="${
                          isExpanded ? 'border-(--sdr-line-strong) bg-(--sdr-selected)' : 'border-base-300'
                        } rounded-lg border transition"
                      >
                        <!-- Header row — clickable to expand -->
                        <div
                          class="flex cursor-pointer items-center justify-between p-4"
                          @click=${() => setExpandedEmployeeId(isExpanded ? null : emp._id)}
                        >
                          <div class="flex items-center gap-3">
                            <span class="icon-[material-symbols--smart-toy] text-base-content/50 text-lg"></span>
                            <div>
                              <div class="flex items-center gap-2">
                                <span class="font-medium">${emp.name}</span>
                                ${
                                  emp.isDefault
                                    ? html`<span class="text-base-content/50 text-xs">Default</span>`
                                    : nothing
                                }
                              </div>
                              <div class="text-base-content/60 text-sm">${emp.role} at ${emp.company}</div>
                            </div>
                          </div>
                          <div class="flex items-center gap-2">
                            <div class="text-base-content/50 flex gap-1 text-xs">
                              ${
                                assignedEmails.length > 0
                                  ? html`<span class="badge badge-ghost badge-xs gap-1"
                                    ><span class="icon-[material-symbols--mail] text-xs"></span
                                    >${assignedEmails.length}</span
                                  >`
                                  : nothing
                              }
                              ${
                                assignedLinkedin.length > 0
                                  ? html`<span class="badge badge-ghost badge-xs gap-1"
                                    ><span class="icon-[material-symbols--share] text-xs"></span
                                    >${assignedLinkedin.length}</span
                                  >`
                                  : nothing
                              }
                            </div>
                            <span
                              class="${isExpanded ? 'icon-[material-symbols--expand-less]' : 'icon-[material-symbols--expand-more]'} text-base-content/40 text-xl"
                            ></span>
                          </div>
                        </div>

                        <!-- Expanded content -->
                        ${
                          isExpanded
                            ? html`
                              <div class="border-base-300 border-t px-6 pt-4 pb-6">
                                <!-- Actions -->
                                <div class="mb-4 flex gap-2">
                                  <button
                                    class="btn btn-ghost btn-sm gap-1"
                                    @click=${() => openEmployeeEditor({ ...emp })}
                                  >
                                    <span class="icon-[material-symbols--edit] text-base"></span> Edit Profile
                                  </button>
                                  <button
                                    class="btn btn-ghost btn-sm text-error gap-1"
                                    @click=${() =>
                                      void deleteSalesRep(emp, {
                                        email: assignedEmails.length,
                                        linkedin: assignedLinkedin.length,
                                      })}
                                  >
                                    <span class="icon-[material-symbols--delete-outline] text-base"></span> Delete
                                  </button>
                                </div>

                                <!-- Email Accounts -->
                                <div class="mb-4">
                                  <div class="mb-2 flex items-center justify-between">
                                    <h3 class="flex items-center gap-2 text-sm font-medium">
                                      <span class="icon-[material-symbols--mail] text-base-content/50 text-lg"></span>
                                      Email accounts
                                    </h3>
                                    <div class="dropdown dropdown-end">
                                      <label
                                        tabindex="0"
                                        class="btn btn-ghost btn-sm gap-1"
                                        ?disabled=${connectingEmail}
                                      >
                                        ${
                                          connectingEmail
                                            ? html`<span class="loading loading-spinner loading-xs"></span>`
                                            : html`<span class="icon-[material-symbols--add] text-base"></span> Add Email`
                                        }
                                      </label>
                                      <ul
                                        tabindex="0"
                                        class="dropdown-content menu rounded-box bg-base-100 z-10 w-48 p-2 shadow"
                                      >
                                        <li>
                                          <a @click=${() => void connectUnipileEmail(emp._id, 'google')}
                                            >Gmail via Unipile</a
                                          >
                                        </li>
                                        <li>
                                          <a @click=${() => void connectUnipileEmail(emp._id, 'microsoft')}
                                            >Outlook via Unipile</a
                                          >
                                        </li>
                                      </ul>
                                    </div>
                                  </div>
                                  ${
                                    loadFailed.email
                                      ? loadFailedNotice('Email accounts', () => void loadAccounts())
                                      : assignedEmails.length === 0
                                      ? html`<div
                                        class="border-base-300 text-base-content/40 rounded-lg border border-dashed p-3 text-center text-xs"
                                      >
                                        No email accounts assigned.
                                      </div>`
                                      : html`<div class="flex flex-col gap-1.5">
                                        ${assignedEmails.map(
                                          acc => html`
                                            <div
                                              class="border-base-300 bg-base-100 flex items-center justify-between rounded-lg border px-4 py-3"
                                            >
                                              <div class="flex items-center gap-3">
                                                <span
                                                  class="icon-[material-symbols--mail] text-base-content/50 text-base"
                                                ></span>
                                                <div>
                                                  <div class="text-sm font-medium">${acc.name}</div>
                                                  <div class="text-base-content/50 text-xs">${acc.email}</div>
                                                </div>
                                              </div>
                                              <div class="flex items-center gap-4">
                                                ${
                                                  acc.provider === 'unipile_email'
                                                    ? html`<label
                                                        class="flex cursor-pointer items-center gap-2"
                                                        title="An already-warmed-up mailbox skips the 15 → 35 → 75 daily ramp and sends at the full daily limit."
                                                      >
                                                        <div class="text-right">
                                                          <div class="text-xs font-medium">Warmed up</div>
                                                          <div class="text-base-content/40 text-xs">
                                                            ${warmupCapLabel(acc.emailWarmedManually === true, acc.dailyLimit)}
                                                          </div>
                                                        </div>
                                                        <input
                                                          type="checkbox"
                                                          class="toggle toggle-primary toggle-sm"
                                                          .checked=${acc.emailWarmedManually === true}
                                                          @change=${(e: Event) =>
                                                            void setMailboxWarmed(
                                                              acc.email,
                                                              (e.target as HTMLInputElement).checked,
                                                            )}
                                                        />
                                                      </label>`
                                                    : ''
                                                }
                                                <div class="text-right">
                                                  <div class="text-lg font-medium tabular-nums">
                                                    ${(acc.totalSent ?? 0).toLocaleString()}
                                                  </div>
                                                  <div class="text-base-content/40 text-xs">Emails sent</div>
                                                </div>
                                                <button
                                                  class="btn btn-ghost btn-xs text-error"
                                                  title=${DISCONNECT_ACCOUNT_HINT}
                                                  aria-label=${`Disconnect ${acc.name}`}
                                                  @click=${() =>
                                                    void disconnectAccount({
                                                      provider: acc.provider ?? 'instantly_email',
                                                      accountId: acc.email,
                                                      channel: 'email',
                                                      label: acc.name,
                                                      repName: emp.name,
                                                      warmedUp: acc.provider === 'unipile_email' && acc.emailWarmedManually === true,
                                                    })}
                                                >
                                                  <span class="icon-[material-symbols--close] text-sm"></span>
                                                </button>
                                              </div>
                                            </div>
                                          `,
                                        )}
                                      </div>`
                                  }
                                </div>

                                <!-- LinkedIn Accounts -->
                                <div>
                                  <div class="mb-2 flex items-center justify-between">
                                    <h3 class="flex items-center gap-2 text-sm font-medium">
                                      <span class="icon-[material-symbols--share] text-base-content/50 text-lg"></span>
                                      LinkedIn accounts
                                    </h3>
                                    <button
                                      class="btn btn-ghost btn-sm gap-1"
                                      ?disabled=${connectingLinkedin}
                                      @click=${() => void connectLinkedin(emp._id)}
                                    >
                                      ${
                                        connectingLinkedin
                                          ? html`<span class="loading loading-spinner loading-xs"></span>`
                                          : html`<span class="icon-[material-symbols--add] text-base"></span> Add LinkedIn`
                                      }
                                    </button>
                                  </div>
                                  ${
                                    loadFailed.linkedin
                                      ? loadFailedNotice('LinkedIn accounts', () => void loadAccounts())
                                      : assignedLinkedin.length === 0
                                      ? html`<div
                                        class="border-base-300 text-base-content/40 rounded-lg border border-dashed p-3 text-center text-xs"
                                      >
                                        No LinkedIn accounts assigned.
                                      </div>`
                                      : html`<div class="flex flex-col gap-1.5">
                                        ${assignedLinkedin.map(
                                          acc => html`
                                            <div
                                              class="border-base-300 bg-base-100 flex items-center justify-between rounded-lg border px-4 py-3"
                                            >
                                              <div class="flex items-center gap-3">
                                                <span class="icon-[material-symbols--share] text-base-content/50 text-base"></span>
                                                <div>
                                                  <div class="flex items-center gap-2">
                                                    <span class="text-sm font-medium">${acc.name}</span>
                                                    ${(() => {
                                                      // The seat decides whether invitations from this
                                                      // account can carry a personal note. Shown here
                                                      // because this is where an operator looks at an
                                                      // account as an account.
                                                      const seat = describeSeat(acc.premiumFeatures);
                                                      return toneDot(seatLook(seat), seat.title);
                                                    })()}
                                                  </div>
                                                  <div class="text-base-content/50 text-xs">
                                                    ${acc.status ?? 'connected'}
                                                  </div>
                                                </div>
                                              </div>
                                              <div class="flex items-center gap-4">
                                                <div class="text-right">
                                                  <div class="text-lg font-medium tabular-nums">
                                                    ${(acc.totalInvitesSent ?? 0).toLocaleString()}
                                                  </div>
                                                  <div class="text-base-content/40 text-xs">Invites</div>
                                                </div>
                                                <div class="text-right">
                                                  <div class="text-lg font-medium tabular-nums">
                                                    ${(acc.totalDmsSent ?? 0).toLocaleString()}
                                                  </div>
                                                  <div class="text-base-content/40 text-xs">DMs</div>
                                                </div>
                                                <button
                                                  class="btn btn-ghost btn-xs text-error"
                                                  title=${DISCONNECT_ACCOUNT_HINT}
                                                  aria-label=${`Disconnect ${acc.name}`}
                                                  @click=${() =>
                                                    void disconnectAccount({
                                                      provider: 'unipile_linkedin',
                                                      accountId: acc.id,
                                                      channel: 'linkedin',
                                                      label: acc.name,
                                                      repName: emp.name,
                                                    })}
                                                >
                                                  <span class="icon-[material-symbols--close] text-sm"></span>
                                                </button>
                                              </div>
                                            </div>
                                          `,
                                        )}
                                      </div>`
                                  }
                                </div>
                              </div>
                            `
                            : nothing
                        }
                      </div>
                    `;
                  })}
                </div>`
          }
          ${
            editingEmployee !== null
              ? html`
                <div class="border-base-300 bg-base-200 mt-4 rounded-lg border p-4">
                  <h3 class="mb-3 text-sm font-medium">
                    ${editingEmployee._id && editingEmployee._id !== autoCreatedRepId ? 'Edit profile' : 'New profile'}
                  </h3>
                  <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Name</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="Sarah Miller"
                        .value=${editingEmployee.name ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({ ...editingEmployee, name: (e.target as HTMLInputElement).value })}
                      />
                    </label>
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Role</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="Business Development Rep"
                        .value=${editingEmployee.role ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({ ...editingEmployee, role: (e.target as HTMLInputElement).value })}
                      />
                    </label>
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Company</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="New Digital Intelligence"
                        .value=${editingEmployee.company ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({ ...editingEmployee, company: (e.target as HTMLInputElement).value })}
                      />
                    </label>
                    <label class="form-control w-full">
                      <div class="label"><span class="label-text">Communication style</span></div>
                      <input
                        type="text"
                        class="input input-bordered w-full"
                        placeholder="professional but warm, concise"
                        .value=${editingEmployee.communicationStyle ?? ''}
                        @input=${(e: InputEvent) =>
                          setEditingEmployee({
                            ...editingEmployee,
                            communicationStyle: (e.target as HTMLInputElement).value,
                          })}
                      />
                    </label>
                  </div>
                  <label class="form-control mt-3 w-full">
                    <div class="label"><span class="label-text">Email sign-off</span></div>
                    <textarea
                      class="textarea textarea-bordered w-full"
                      rows="3"
                      placeholder="Best regards,&#10;Sarah Miller&#10;Business Development"
                      .value=${editingEmployee.signOff ?? ''}
                      @input=${(e: InputEvent) =>
                        setEditingEmployee({ ...editingEmployee, signOff: (e.target as HTMLTextAreaElement).value })}
                    ></textarea>
                  </label>
                  <label class="form-control mt-3 w-full">
                    <div class="label"><span class="label-text">LinkedIn DM sign-off</span></div>
                    <p class="text-base-content/60 mb-1 text-xs leading-snug">
                      Used on LinkedIn messages. Leave empty to reuse the email sign-off. Connection requests
                      never carry a sign-off.
                    </p>
                    <textarea
                      class="textarea textarea-bordered w-full"
                      rows="2"
                      placeholder="– Sarah"
                      .value=${editingEmployee.linkedinSignOff ?? ''}
                      @input=${(e: InputEvent) =>
                        setEditingEmployee({
                          ...editingEmployee,
                          linkedinSignOff: (e.target as HTMLTextAreaElement).value,
                        })}
                    ></textarea>
                  </label>
                  <div class="border-base-300 mt-3 rounded-lg border border-dashed p-3">
                    <div class="mb-2 flex items-center justify-between gap-2">
                      <span class="text-sm font-medium">Sending accounts</span>
                      <div class="flex gap-2">
                        <div class="dropdown dropdown-end">
                          <label tabindex="0" class="btn btn-ghost btn-xs gap-1" ?disabled=${connectingEmail}>
                            ${
                              connectingEmail
                                ? html`<span class="loading loading-spinner loading-xs"></span>`
                                : html`<span class="icon-[material-symbols--mail] text-sm"></span> Add Email`
                            }
                          </label>
                          <ul
                            tabindex="0"
                            class="dropdown-content menu rounded-box bg-base-100 z-10 w-48 p-2 shadow"
                          >
                            <li>
                              <a
                                @click=${() => {
                                  if (nameMissing(editingEmployee)) {
                                    notifyError(NAME_FIRST);
                                    return;
                                  }
                                  void connectUnipileEmail(ensureEditingRepPersisted, 'google');
                                }}
                                >Gmail via Unipile</a
                              >
                            </li>
                            <li>
                              <a
                                @click=${() => {
                                  if (nameMissing(editingEmployee)) {
                                    notifyError(NAME_FIRST);
                                    return;
                                  }
                                  void connectUnipileEmail(ensureEditingRepPersisted, 'microsoft');
                                }}
                                >Outlook via Unipile</a
                              >
                            </li>
                          </ul>
                        </div>
                        <button
                          class="btn btn-ghost btn-xs gap-1"
                          ?disabled=${connectingLinkedin}
                          @click=${() => {
                            if (nameMissing(editingEmployee)) {
                              notifyError(NAME_FIRST);
                              return;
                            }
                            void connectLinkedin(ensureEditingRepPersisted);
                          }}
                        >
                          ${
                            connectingLinkedin
                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                              : html`<span class="icon-[material-symbols--share] text-sm"></span> Add LinkedIn`
                          }
                        </button>
                      </div>
                    </div>
                    ${(() => {
                      const repId = editingEmployee._id;
                      const emails = repId ? emailAccounts.filter(a => a.aiEmployeeId === repId) : [];
                      const linkedins = repId ? linkedinAccounts.filter(a => a.aiEmployeeId === repId) : [];
                      if (loadFailed.email || loadFailed.linkedin) {
                        return loadFailedNotice('Sending accounts', () => void loadAccounts());
                      }
                      if (emails.length === 0 && linkedins.length === 0) {
                        return html`
                          <div class="alert alert-warning px-3 py-2 text-xs">
                            <span class="icon-[material-symbols--warning] text-base"></span>
                            Connect at least one email or LinkedIn account to create this Sales Representative.
                          </div>`;
                      }
                      return html`
                        <div class="flex flex-wrap gap-1.5">
                          ${emails.map(
                            acc => html`<span class="badge badge-ghost badge-sm gap-1"
                              ><span class="icon-[material-symbols--mail] text-xs"></span>${acc.email}</span
                            >`,
                          )}
                          ${linkedins.map(
                            acc => html`<span class="badge badge-ghost badge-sm gap-1"
                              ><span class="icon-[material-symbols--share] text-xs"></span>${acc.name}</span
                            >`,
                          )}
                        </div>`;
                    })()}
                  </div>
                  <div class="mt-3 flex items-center gap-4">
                    <label class="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        class="checkbox checkbox-sm"
                        .checked=${editingEmployee.isDefault ?? false}
                        @change=${(e: Event) =>
                          setEditingEmployee({ ...editingEmployee, isDefault: (e.target as HTMLInputElement).checked })}
                      />
                      <span class="text-sm">Set as default</span>
                    </label>
                    <div class="flex-1"></div>
                    <button class="btn btn-ghost btn-sm" @click=${() => void cancelEmployeeEditor()}>Cancel</button>
                    <button
                      class="btn btn-primary btn-sm"
                      ?disabled=${savingEmployee || !repHasAccount(editingEmployee._id)}
                      title=${
                        repHasAccount(editingEmployee._id)
                          ? ''
                          : loadFailed.email || loadFailed.linkedin
                            ? 'Sending accounts could not be loaded.'
                            : 'Connect at least one email or LinkedIn account first.'
                      }
                      @click=${() => void saveSalesRep()}
                    >
                      ${savingEmployee ? html`<span class="loading loading-spinner loading-xs"></span>` : nothing} Save
                    </button>
                  </div>
                </div>
              `
              : nothing
          }
        </div>
      </div>

      <!-- CRM Integrations -->
      <div class="card bg-base-100">
        <div class="card-body p-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-3">
              <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
                <span class="icon-[material-symbols--hub] text-xl"></span>
              </span>
              <div>
                <h2 class="text-[0.9375rem] font-medium">CRM integrations</h2>
                <p class="text-base-content/70 text-sm">
                  Sync contacted leads, replies and activities to your CRM. Pushes happen asynchronously in the
                  background.
                </p>
              </div>
            </div>
          </div>

          <div class="mt-4 flex flex-col gap-3">
            <!-- HubSpot row -->
            <div class="border-base-300 flex items-center justify-between rounded-lg border px-4 py-3">
              <div class="flex items-center gap-3">
                <span class="bg-base-200 text-base-content/60 flex h-9 w-9 items-center justify-center rounded-lg">
                  <span class="icon-[material-symbols--hub] text-lg"></span>
                </span>
                <div>
                  <div class="font-medium">HubSpot</div>
                  ${
                    loadFailed.crmConfig
                      ? html`<div class="mt-1">${loadFailedNotice("HubSpot's status", () => void loadAccounts())}</div>`
                      : !loadedOnce
                      ? html`<div class="mt-1">${skeletonLine({ width: 'w-48' })}</div>`
                      : crmConfig.hubspot.enabled
                      ? crmConfig.hubspot.mode === 'private_app'
                        ? html`<div class="text-base-content/60 text-xs">
                          Configured via server-side Private App token. No user action required.
                        </div>`
                        : html`<div class="text-base-content/60 text-xs">
                          Push contacts and activity notes to a HubSpot portal.
                        </div>`
                      : html`<div class="text-warning text-xs">
                        Not configured on the server. Set <code>HUBSPOT_PRIVATE_APP_TOKEN</code> (recommended) or
                        <code>HUBSPOT_CLIENT_ID</code> + <code>HUBSPOT_CLIENT_SECRET</code> for OAuth.
                      </div>`
                  }
                </div>
              </div>
              ${
                crmConfig.hubspot.mode === 'private_app'
                  ? html`<div class="flex items-center gap-2">
                    ${toneDot({ tone: 'positive', label: 'Active' })}
                    ${
                      crmConfig.hubspot.oauthAvailable
                        ? html`<button
                          class="btn btn-xs gap-1"
                          ?disabled=${connectingCrm}
                          title="Additionally connect a per-user HubSpot account via OAuth (multi-tenant)"
                          @click=${() => void connectHubspot()}
                        >
                          ${
                            connectingCrm
                              ? html`<span class="loading loading-spinner loading-xs"></span>`
                              : html`<span class="icon-[material-symbols--add-link] text-sm"></span>`
                          }
                          Connect another
                        </button>`
                        : nothing
                    }
                  </div>`
                  : html`<button
                    class="btn btn-primary btn-sm gap-1"
                    ?disabled=${!crmConfig.hubspot.enabled || connectingCrm}
                    @click=${() => void connectHubspot()}
                  >
                    ${
                      connectingCrm
                        ? html`<span class="loading loading-spinner loading-xs"></span>`
                        : html`<span class="icon-[material-symbols--add-link] text-base"></span>`
                    }
                    Connect HubSpot
                  </button>`
              }
            </div>

            <!-- Active connections list -->
            ${
              loadFailed.crmConnections
                ? loadFailedNotice('CRM connections', () => void loadAccounts())
                : !loadedOnce
                ? skeletonLine({ height: 'h-10' })
                : crmConnections.length === 0
                ? html`<div
                  class="border-base-300 text-base-content/40 rounded-lg border border-dashed p-3 text-center text-xs"
                >
                  No CRM connected yet.
                </div>`
                : html`<div class="flex flex-col gap-1.5">
                  ${crmConnections.map(
                    conn => html`
                      <div class="bg-base-200 rounded-lg px-4 py-3">
                        <div class="flex items-center justify-between">
                          <div class="flex items-center gap-3">
                            <span class="icon-[material-symbols--hub] text-base-content/50 text-base"></span>
                            <div>
                              <div class="text-sm font-medium">
                                ${conn.provider === 'hubspot' ? 'HubSpot' : conn.provider} ·
                                ${conn.accountLabel ?? conn.accountId ?? ''}${
                                  conn.isSystem
                                    ? html` <span class="badge badge-ghost badge-xs ml-1">system</span>`
                                    : nothing
                                }
                              </div>
                              <div class="text-base-content/50 text-xs">
                                Status:
                                ${conn.status}${
                                  conn.lastUsedAt
                                    ? html` · last used ${new Date(conn.lastUsedAt).toLocaleString()}`
                                    : nothing
                                }
                              </div>
                              ${conn.lastError ? html`<div class="text-error text-xs">${conn.lastError}</div>` : nothing}
                            </div>
                          </div>
                          <div class="flex items-center gap-1">
                            ${
                              conn.provider === 'hubspot'
                                ? html`<button
                                  class="btn btn-ghost btn-xs gap-1"
                                  title="Map our lead states to this portal's Lead Status options"
                                  @click=${() => void toggleMapping(conn._id)}
                                >
                                  <span class="icon-[material-symbols--rule] text-sm"></span>
                                  Lead Status
                                  <span
                                    class="icon-[material-symbols--expand-more] text-sm transition-transform ${
                                      mappingConnId === conn._id ? 'rotate-180' : ''
                                    }"
                                  ></span>
                                </button>`
                                : nothing
                            }
                            ${
                              conn.isSystem
                                ? html`<span class="text-base-content/40 text-xs" title="Managed via server env"
                                  >env-managed</span
                                >`
                                : html`<button
                                  class="btn btn-ghost btn-xs text-error"
                                  title="Disconnect"
                                  @click=${() => void disconnectCrm(conn._id, conn.accountLabel ?? conn.provider)}
                                >
                                  <span class="icon-[material-symbols--close] text-sm"></span>
                                </button>`
                            }
                          </div>
                        </div>
                        ${mappingConnId === conn._id ? renderMappingPanel() : nothing}
                      </div>
                    `,
                  )}
                </div>`
            }
            <!-- Locked / coming-soon CRM providers (UI only) -->
            ${lockedCrmProviders.map(
              prov => html`
                <div
                  class="group border-base-300 relative cursor-not-allowed overflow-hidden rounded-lg border px-4 py-3 opacity-70 transition hover:opacity-100"
                  aria-disabled="true"
                  title="Please contact us to unlock"
                >
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <span
                        class="bg-base-200 text-base-content/50 flex h-9 w-9 items-center justify-center rounded-lg"
                      >
                        <span class="${prov.icon} text-lg"></span>
                      </span>
                      <div>
                        <div class="text-base-content/70 flex items-center gap-2 font-medium">
                          ${prov.name}
                          <span class="icon-[material-symbols--lock] text-base-content/40 text-sm"></span>
                        </div>
                        <div class="text-base-content/50 text-xs">Coming soon</div>
                      </div>
                    </div>
                    <span class="badge badge-ghost border-base-300 text-base-content/60 gap-1">
                      <span class="icon-[material-symbols--lock] text-xs"></span>
                      Unlock on Request
                    </span>
                  </div>

                  <!-- Hover overlay -->
                  <div
                    class="bg-base-100/85 text-base-content pointer-events-none absolute inset-0 flex items-center justify-center text-sm font-medium opacity-0 backdrop-blur-[1px] transition-opacity duration-200 group-hover:opacity-100"
                  >
                    <span class="flex items-center gap-2">
                      <span class="icon-[material-symbols--mail] text-base"></span>
                      Please contact us to unlock
                    </span>
                  </div>
                </div>
              `,
            )}
          </div>

          <p class="text-base-content/50 mt-3 text-xs">
            After connecting, open a campaign and use “Push to CRM” to send its contacted leads and replies as contacts
            + notes.
          </p>
        </div>
      </div>

      ${
        assessmentOn
          ? html`
              <!-- Assessment Agent -->
              <!-- Drawn with layout utilities and the theme's variables, no daisyUI and no icon (the ratchet, R12a);
                   a plain link, so this page does not import the router that renders it. -->
              <div class="rounded-xl border border-(--sdr-line) bg-(--color-base-100) p-6">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <h2 class="text-[0.9375rem] font-medium">Assessment Agent</h2>
                    <p class="text-sm text-(--sdr-ink-muted)">A business case PDF per lead, from your own calculator.</p>
                  </div>
                  <a
                    href="/assessment"
                    class="inline-flex h-8 items-center rounded-lg border border-(--color-primary) bg-(--color-primary) px-3 text-sm font-medium text-(--color-primary-content) transition-colors hover:border-(--color-primary-hover) hover:bg-(--color-primary-hover)"
                  >
                    Open
                  </a>
                </div>
              </div>
            `
          : nothing
      }

      <!-- Deleted Campaigns (recycle bin) -->
      <div class="card bg-base-100">
        <div class="card-body p-6">
          <div class="flex items-center gap-3">
            <span class="bg-base-200 text-base-content/60 flex h-10 w-10 items-center justify-center rounded-lg">
              <span class="icon-[material-symbols--delete-outline] text-xl"></span>
            </span>
            <div>
              <h2 class="text-[0.9375rem] font-medium">Deleted campaigns</h2>
              <p class="text-base-content/70 text-sm">
                Deleted campaigns stop sending and are hidden from your dashboard, but stay here for
                ${retentionDays} days. Restore one at any point in that window — afterwards it is deleted
                permanently, along with its leads and messages.
              </p>
            </div>
          </div>

          ${
            loadingDeleted
              ? html`<div class="mt-4 flex flex-col gap-2">${skeletonLine()} ${skeletonLine()}</div>`
              : deletedFailed
                ? html`<div class="mt-4">${loadFailedNotice('Deleted campaigns', () => void loadDeletedCampaigns())}</div>`
                : deletedCampaigns.length === 0
                ? html`
                  <div class="text-base-content/60 mt-4 flex items-center gap-2 text-sm">
                    <span class="icon-[material-symbols--check-circle] text-base-content/40 text-lg"></span>
                    No deleted campaigns.
                  </div>
                `
                : html`
                  <div class="mt-4 flex flex-col gap-2">
                    ${deletedCampaigns.map(c => {
                      const daysLeft = c.daysLeft ?? 0;
                      // Under a week left is the point at which "you still have
                      // time" stops being true — flag it rather than letting a
                      // campaign quietly reach its purge date.
                      const urgent = daysLeft <= 7;
                      const busy = deletedBusy === c._id;
                      return html`
                        <div class="border-base-300 flex items-center gap-3 rounded-lg border px-3 py-2">
                          <span class="icon-[material-symbols--campaign] text-base-content/40 text-xl shrink-0"></span>
                          <div class="min-w-0 flex-1">
                            <div class="truncate text-sm font-medium">${c.name || 'Untitled Draft'}</div>
                            <div class="text-base-content/50 text-xs">
                              Deleted ${new Date(c.deletedAt).toLocaleDateString()} ·
                              <span class=${urgent ? 'text-error font-medium' : ''}>
                                ${
                                  daysLeft === 0
                                    ? 'deleted permanently within hours'
                                    : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`
                                }
                              </span>
                              ${c.leadsProcessed ? html` · ${c.leadsProcessed} leads` : nothing}
                            </div>
                          </div>
                          <button
                            class="btn btn-xs gap-1 ${busy ? 'btn-disabled' : ''}"
                            title="Restore this campaign to your dashboard"
                            @click=${() => void restoreCampaign(c)}
                          >
                            ${
                              busy
                                ? html`<span class="loading loading-spinner loading-xs"></span>`
                                : html`<span class="icon-[material-symbols--restore-from-trash] text-sm"></span>`
                            }
                            Restore
                          </button>
                          <button
                            class="btn btn-ghost btn-xs text-error gap-1 ${busy ? 'btn-disabled' : ''}"
                            title="Delete this campaign permanently — cannot be undone"
                            @click=${() => void purgeCampaign(c)}
                          >
                            <span class="icon-[material-symbols--delete-forever] text-sm"></span>
                            Delete permanently
                          </button>
                        </div>
                      `;
                    })}
                  </div>
                `
          }
        </div>
      </div>
    </div>
  `;
});
